import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Grid, IconButton, Skeleton
} from '@mui/material';
import { CheckSharp, Cancel, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useGetOrder, useGetPayloadMetaData } from '../../../../logic/useApiQuery';
import { useParams, useLocation } from 'react-router-dom';
import { PayloadTransaction } from '../../../../types';
import dayjs from 'dayjs';
import { useState } from 'react';
import { usePayloadProcessingState } from '../../../context/payload-processing-state/PayloadProcessingState';
import { getTransactionsTotal, getCashToCloseRemaining } from '../../../../utils/getTransactionsTotal';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { useContent } from '../../../../logic';
import { isAdhocPortalOrder } from '../../../../utils/isAdhocPortalOrder';

const getStatus = (status: PayloadTransaction['status']): 'checkMark' | 'xMark' => {
  const checkMarkStatuses = ['authorized', 'pending', 'processed']
  if (status && checkMarkStatuses.includes(status)) {
    return 'checkMark'
  }
  return 'xMark'
}

const formatAccountNumber = (accountNumber: string) => {
  return accountNumber.replace(/[A-Za-z]/g, '')
}

export function CashToCloseTransactionsSummary({expandedByDefault}: {expandedByDefault: boolean}) {

  const { closingId, id } = useParams()
  const { search } = useLocation()
  const { TaskText } = useContent()
  const { data: transactions } = useGetPayloadMetaData(closingId ?? '', id ?? '', search ?? '')
  const { data: orderData } = useGetOrder(closingId ?? '', search ?? '')
  const [expanded, setExpanded] = useState(expandedByDefault)

  const { payloadState } = usePayloadProcessingState()
  const payloadProcessing = payloadState === 'processing'

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const cashToCloseAmountExists = orderData && !isAdhocPortalOrder(orderData.Order) && !!orderData?.Order.CashToCloseDetails?.amount && Number(orderData?.Order.CashToCloseDetails?.amount) > 0

  if (!transactions) return <></>

  if (!orderData) return null
  if (isAdhocPortalOrder(orderData.Order)) return null

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Payment Summary
          <IconButton onClick={toggleExpanded}>
            { expanded ? <ExpandLess/> : <ExpandMore/> }
          </IconButton>
        </Typography>
        { expanded && <Grid container spacing={1}>
          <Grid item xs={12} sm={4} md={12} lg={4}>
            { cashToCloseAmountExists &&
            <Box
              sx={{
                backgroundColor: '#e7e7e7',
                padding: 2,
                height: '100%',
                borderRadius: '8px',
                border: '1px solid #000000',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="body1">
                Total cash to close
              </Typography>
              <Typography sx={{ fontSize: '22px', fontWeight: 'bold' }}>
                { formatCurrency(Number(orderData?.Order.CashToCloseDetails?.amount)) }
              </Typography>
            </Box> }
          </Grid>
          <Grid item xs={12} sm={cashToCloseAmountExists ? 4 : 12} md={cashToCloseAmountExists ? 6 : 12} lg={cashToCloseAmountExists ? 4 : 12}>
            <Box
              sx={{
                backgroundColor: '#e7e7e7',
                padding: 2,
                height: '100%',
                borderRadius: '8px',
                border: '1px solid #000000',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="body1">
                Amount paid in this {TaskText}
              </Typography>
              <Typography sx={{ fontSize: '22px', fontWeight: 'bold' }}>
                {formatCurrency(getTransactionsTotal(transactions))}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={cashToCloseAmountExists ? 4 : 12} md={6} lg={cashToCloseAmountExists ? 4 : 12}>
            {  cashToCloseAmountExists &&
            <Box
              sx={{
                backgroundColor: '#E3F2FD',
                padding: 2,
                height: '100%',
                borderRadius: '8px',
                border: '1px solid #90CAF9',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="body1">
                Amount remaining
              </Typography>
              <Typography sx={{ fontSize: '22px', fontWeight: 'bold' }}>
                {
                  formatCurrency(
                    Math.max(
                      0,
                      getCashToCloseRemaining(orderData?.Order.CashToCloseDetails?.amount, transactions)
                    )
                  )
                }
              </Typography>
            </Box> }
          </Grid>
        </Grid> }
      </Box>
      {(transactions.length > 0 || payloadProcessing) && expanded &&
      <Box>
        <Typography variant="h6" gutterBottom component="div">
          Payment History
        </Typography>
        <TableContainer>
          <Table aria-label="transaction table">
            <TableHead>
              <TableRow>
                <TableCell>Time Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow
                  key={transaction.transactionId}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {dayjs(transaction.createdAt).format('MM-DD-YY hh:mm A')}
                  </TableCell>
                  <TableCell>
                    {getStatus(transaction.status) === 'checkMark' ? (
                      <CheckSharp color="success" />
                    ) : (
                      <Cancel color="error" />
                    )
                    }
                  </TableCell>
                  <TableCell>
                    {transaction.accountNumber ? `...${formatAccountNumber(transaction.accountNumber)}` : '?'}
                  </TableCell>
                  <TableCell
                    align="right"
                  >
                    {formatCurrency(transaction.amount)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Skeleton row - only appears during mutation */}
              {payloadProcessing && (
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>
                    <Skeleton animation="wave" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width={24} height={24} variant="circular" />
                  </TableCell>
                  <TableCell>
                    <Skeleton animation="wave" width="60%" />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton animation="wave" width="70%" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box> }
    </Box>
  )
}
